// ---------------------------------------------------------------------------
// i18n.config.ts — Internationalization configuration
// ---------------------------------------------------------------------------
// Supported locales, default locale, RTL locale list, and locale metadata.
// ---------------------------------------------------------------------------

export interface LocaleConfig {
  /** ISO 639-1 language code */
  code: string;
  /** Native language name */
  nativeName: string;
  /** English language name */
  englishName: string;
  /** ISO 3166-1 alpha-2 country code for flag display */
  flagCode: string;
  /** Text direction */
  dir: 'ltr' | 'rtl';
}

/** Default application locale */
export const DEFAULT_LOCALE = 'en' as const;

/** All supported locale codes */
export const SUPPORTED_LOCALES = [
  'en',
  'ar',
  'th',
  'es',
  'fr',
  'de',
  'ja',
  'zh',
  'hi',
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** Locales that use right-to-left text direction */
export const RTL_LOCALES: ReadonlySet<SupportedLocale> = new Set(['ar']);

/** Full locale metadata for UI display */
export const LOCALE_MAP: Record<SupportedLocale, LocaleConfig> = {
  en: {
    code: 'en',
    nativeName: 'English',
    englishName: 'English',
    flagCode: 'US',
    dir: 'ltr',
  },
  ar: {
    code: 'ar',
    nativeName: '\u0627\u0644\u0639\u0631\u0628\u064A\u0629',
    englishName: 'Arabic',
    flagCode: 'SA',
    dir: 'rtl',
  },
  th: {
    code: 'th',
    nativeName: '\u0E44\u0E17\u0E22',
    englishName: 'Thai',
    flagCode: 'TH',
    dir: 'ltr',
  },
  es: {
    code: 'es',
    nativeName: 'Espa\u00F1ol',
    englishName: 'Spanish',
    flagCode: 'ES',
    dir: 'ltr',
  },
  fr: {
    code: 'fr',
    nativeName: 'Fran\u00E7ais',
    englishName: 'French',
    flagCode: 'FR',
    dir: 'ltr',
  },
  de: {
    code: 'de',
    nativeName: 'Deutsch',
    englishName: 'German',
    flagCode: 'DE',
    dir: 'ltr',
  },
  ja: {
    code: 'ja',
    nativeName: '\u65E5\u672C\u8A9E',
    englishName: 'Japanese',
    flagCode: 'JP',
    dir: 'ltr',
  },
  zh: {
    code: 'zh',
    nativeName: '\u4E2D\u6587',
    englishName: 'Chinese',
    flagCode: 'CN',
    dir: 'ltr',
  },
  hi: {
    code: 'hi',
    nativeName: '\u0939\u093F\u0928\u094D\u0926\u0940',
    englishName: 'Hindi',
    flagCode: 'IN',
    dir: 'ltr',
  },
};

/** localStorage key for persisted language preference */
export const LOCALE_STORAGE_KEY = 'hms-locale' as const;

/**
 * Checks whether a given string is a supported locale code.
 */
export function isSupportedLocale(value: string): value is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Content map type: English required, other locales optional (fallback to en).
 * Used by page content dictionaries so new locales fall back gracefully.
 */
export type LocaleContentMap<T> = { en: T } & Partial<Record<SupportedLocale, T>>;

/**
 * Returns the text direction for a given locale.
 */
export function getLocaleDir(locale: SupportedLocale): 'ltr' | 'rtl' {
  return RTL_LOCALES.has(locale) ? 'rtl' : 'ltr';
}
